import {
  Cloud,
  CloudArrowUp,
  DownloadSimple,
  FileCloud,
  FilePlus,
  Queue,
} from '@phosphor-icons/react';
import {
  ControlButton,
  PageControlsContainer,
  ProjectControls,
} from './styles';
import { useContext, useEffect, useState } from 'react';
import { TtsContext } from '../../../contexts/TtsContext';
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
import { DownloadModal } from './DownloadModal';
import { OpenProjectModal } from './OpenProjectModal';
import { SaveProjectModal } from './SaveProjectModal';
import { Spinner } from '@fluentui/react';
import { callFunction } from '../../../lib/Helpers';
import { generateSsml } from '../../../lib/generateSsml';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import JSZip from 'jszip';
import FileSaver from 'file-saver';
import { TeamsFxContext } from '../../../Context';
import { toast } from 'react-toastify';
import { useStore } from '../../../store';
import { useTranslation } from 'react-i18next';
import { app } from '@microsoft/teams-js';

export const PageControls = () => {
  const appInsights = useAppInsightsContext();
  const [context, setContext] = useState<app.Context>();

  useEffect(() => {
    app.getContext().then((context) => {
      setContext(context);
    });
  }, []);

  const teamsUserCredential = useContext(TeamsFxContext).teamsUserCredential;

  const { t } = useTranslation('myapps');

  const { blocks, createProject, isLoading, setIsLoading, projectInfo } =
    useStore();

  const { speechToken, setAudioTrack, limitReached } = useContext(TtsContext);

  const [format, setFormat] = useState<string>('24-mp3');
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showOpenProjectModal, setShowOpenProjectModal] = useState(false);
  const [showSaveProjectModal, setShowSaveProjectModal] = useState(false);

  const saveChanges = () => {
    setIsLoading(true);

    if (projectInfo) {
      const params = {
        id: projectInfo.id,
        name: projectInfo.name,
        description: projectInfo.description,
        data: blocks,
      };

      callFunction(
        true,
        'patchProject?{0}',
        JSON.stringify(params),
        false,
        teamsUserCredential
      )
        .then(() => toast.success('Changes saved successfully.'))
        .catch((error) => {
          if (process.env.REACT_APP_APP_INSIGHTS !== '') {
            appInsights.trackException({
              exception: error,
              properties: { component: 'TtsApp', location: 'onSaveProject' },
            });
          }
        })
        .finally(() => setIsLoading(false));
    }
  };

  const handleSaveProject = () => {
    if (limitReached) {
      toast('Characters limit reached', { type: 'error' });
      return;
    }

    if (!projectInfo?.id) {
      setShowSaveProjectModal(true);
    } else {
      saveChanges();
    }
  };

  function saveProjectAs() {
    if (limitReached) {
      toast('Characters limit reached', { type: 'error' });
      return;
    }

    setShowSaveProjectModal(true);
  }

  function handlePlayPage() {
    if (limitReached) {
      toast('Characters limit reached', { type: 'error' });
      return;
    }

    const ssml = generateSsml(blocks);

    if (speechToken) {
      try {
        const speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(
          speechToken.token,
          speechToken.region
        );

        speechConfig.speechSynthesisOutputFormat =
          SpeechSDK.SpeechSynthesisOutputFormat.Audio24Khz48KBitRateMonoMp3;

        const synthesizer = new SpeechSDK.SpeechSynthesizer(
          speechConfig,
          null as unknown as SpeechSDK.AudioConfig
        );

        synthesizer.synthesisCompleted = (s, e) => {
          s.close();
          const url = window.URL.createObjectURL(
            new Blob([e.result.audioData])
          );
          setAudioTrack(url);
        };

        synthesizer.SynthesisCanceled = (s, e) => {
          s.close();
        };

        synthesizer.speakSsmlAsync(ssml);
        if (process.env.REACT_APP_APP_INSIGHTS !== '')
          appInsights.trackEvent(
            { name: 'Audio Synthesis iniated' },
            {
              tid: context?.user?.tenant?.id,
              uid: context?.user?.id,
              datasize: ssml.length,
            }
          );
      } catch (err: any) {
        if (process.env.REACT_APP_APP_INSIGHTS !== '')
          appInsights.trackException({
            exception: err,
            properties: {
              component: 'TtsApp',
              location: 'onGenerateAudio',
              isAudioready: false,
            },
          });
      }
    }
  }

  function handleDownload(includeText: boolean, separateAudios: boolean) {
    const filenamebase = projectInfo?.name ?? 'BitFractalTTS';

    const ssmlArray = separateAudios
      ? blocks.map((block) => generateSsml([block]))
      : generateSsml(blocks);

    if (speechToken) {
      try {
        const speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(
          speechToken.token,
          speechToken.region
        );

        let extension = '.mp3';
        let outputformat: SpeechSDK.SpeechSynthesisOutputFormat =
          SpeechSDK.SpeechSynthesisOutputFormat.Audio24Khz48KBitRateMonoMp3;

        switch (format) {
          case '8-wav':
            extension = '.wav';
            outputformat =
              SpeechSDK.SpeechSynthesisOutputFormat.Riff8Khz16BitMonoPcm;
            break;
          case '16-wav':
            extension = '.wav';
            outputformat =
              SpeechSDK.SpeechSynthesisOutputFormat.Riff16Khz16BitMonoPcm;
            break;
          case '24-wav':
            extension = '.wav';
            outputformat =
              SpeechSDK.SpeechSynthesisOutputFormat.Riff24Khz16BitMonoPcm;
            break;
          case '48-wav':
            extension = '.wav';
            outputformat =
              SpeechSDK.SpeechSynthesisOutputFormat.Riff48Khz16BitMonoPcm;
            break;
          case '16-mp3':
            extension = '.mp3';
            outputformat =
              SpeechSDK.SpeechSynthesisOutputFormat.Audio16Khz32KBitRateMonoMp3;
            break;
          case '24-mp3':
            extension = '.mp3';
            outputformat =
              SpeechSDK.SpeechSynthesisOutputFormat.Audio24Khz48KBitRateMonoMp3;
            break;
          case '48-mp3':
            extension = '.mp3';
            outputformat =
              SpeechSDK.SpeechSynthesisOutputFormat.Audio48Khz96KBitRateMonoMp3;
            break;
          case '16-ogg':
            extension = '.ogg';
            outputformat =
              SpeechSDK.SpeechSynthesisOutputFormat.Ogg16Khz16BitMonoOpus;
            break;
          case '24-ogg':
            extension = '.ogg';
            outputformat =
              SpeechSDK.SpeechSynthesisOutputFormat.Ogg24Khz16BitMonoOpus;
            break;
          case '48-ogg':
            extension = '.ogg';
            outputformat =
              SpeechSDK.SpeechSynthesisOutputFormat.Ogg48Khz16BitMonoOpus;
            break;
          default:
            break;
        }

        speechConfig.speechSynthesisOutputFormat = outputformat;

        if (!separateAudios && !includeText) {
          const synthesizer = new SpeechSDK.SpeechSynthesizer(
            speechConfig,
            null as unknown as SpeechSDK.AudioConfig
          );

          synthesizer.synthesisCompleted = (s, e) => {
            s.close();
            const url = window.URL.createObjectURL(
              new Blob([e.result.audioData])
            );

            const a = document.createElement('a');
            a.href = url;
            a.download = filenamebase + ' (' + format + ')' + extension;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
              document.body.removeChild(a);
            }, 0);
          };

          synthesizer.SynthesisCanceled = (s, e) => {
            s.close();
          };

          synthesizer.speakSsmlAsync(ssmlArray as string);

          if (process.env.REACT_APP_APP_INSIGHTS !== '')
            appInsights.trackEvent(
              { name: 'Audio Synthesis for download iniated' },
              {
                tid: context?.user?.tenant?.id,
                uid: context?.user?.id,
                datasize: ssmlArray.length,
              }
            );
        } else {
          if (includeText && !separateAudios) {
            const synthesizer = new SpeechSDK.SpeechSynthesizer(
              speechConfig,
              null as unknown as SpeechSDK.AudioConfig
            );

            const zip = new JSZip();
            const filename = filenamebase + '.zip';

            zip.file(
              `${filenamebase}_Text.txt`,
              blocks.map((block) => block.html).join('\n')
            );

            synthesizer.synthesisCompleted = (s, e) => {
              s.close();
              const url = window.URL.createObjectURL(
                new Blob([e.result.audioData])
              );

              var blob = fetch(url).then((r) => r.blob());
              zip.file(`${filenamebase}_Audio${extension}`, blob);

              zip
                .generateAsync({ type: 'blob' })
                .then((content) => {
                  FileSaver.saveAs(content, filename);

                  if (process.env.REACT_APP_APP_INSIGHTS !== '')
                    appInsights.trackEvent(
                      { name: 'Audio download iniated' },
                      { tid: context?.user?.tenant?.id, uid: context?.user?.id }
                    );
                })
                .catch((reason: any) => {
                  setIsLoading(false);
                  toast.error(
                    'File download error. Please try again and if the error persist contact our support team.'
                  );
                  if (process.env.REACT_APP_APP_INSIGHTS !== '')
                    appInsights.trackException({
                      exception: reason,
                      properties: {
                        component: 'TtsApp',
                        location: 'onGenerateDownloadableAudio',
                      },
                    });
                });
            };

            synthesizer.SynthesisCanceled = (s, e) => {
              s.close();
            };

            synthesizer.speakSsmlAsync(ssmlArray as string);

            if (process.env.REACT_APP_APP_INSIGHTS !== '')
              appInsights.trackEvent(
                { name: 'Audio Synthesis for download iniated' },
                {
                  tid: context?.user?.tenant?.id,
                  uid: context?.user?.id,
                  datasize: ssmlArray.length,
                }
              );
          }

          if (separateAudios && Array.isArray(ssmlArray)) {
            ssmlArray.forEach((ssml, index) => {
              const synthesizer = new SpeechSDK.SpeechSynthesizer(
                speechConfig,
                null as unknown as SpeechSDK.AudioConfig
              );

              synthesizer.synthesisCompleted = (s, e) => {
                var zip = new JSZip();
                const filename = `${filenamebase}-${index + 1}.zip`;

                s.close();
                const url = window.URL.createObjectURL(
                  new Blob([e.result.audioData])
                );

                var blob = fetch(url).then((r) => r.blob());

                if (includeText) {
                  const output = blocks[index].html;
                  zip.file(`${filenamebase}-${index + 1}_Text.txt`, output);
                }

                zip.file(
                  `${filenamebase}-${index + 1}_Audio${extension}`,
                  blob
                );

                zip
                  .generateAsync({ type: 'blob' })
                  .then((content) => {
                    FileSaver.saveAs(content, filename);
                    if (process.env.REACT_APP_APP_INSIGHTS !== '')
                      appInsights.trackEvent(
                        { name: 'Audio download iniated' },
                        {
                          tid: context?.user?.tenant?.id,
                          uid: context?.user?.id,
                        }
                      );
                  })
                  .catch((reason: any) => {
                    setIsLoading(false);
                    toast.error(
                      'File download error. Please try again and if the error persist contact our support team.'
                    );
                    if (process.env.REACT_APP_APP_INSIGHTS !== '')
                      appInsights.trackException({
                        exception: reason,
                        properties: {
                          component: 'TtsApp',
                          location: 'onGenerateDownloadableAudio',
                        },
                      });
                  });
              };

              synthesizer.SynthesisCanceled = (s, e) => {
                s.close();
              };

              synthesizer.speakSsmlAsync(ssml);
              if (process.env.REACT_APP_APP_INSIGHTS !== '')
                appInsights.trackEvent(
                  { name: 'Audio Synthesis for download iniated' },
                  {
                    tid: context?.user?.tenant?.id,
                    uid: context?.user?.id,
                    datasize: ssml.length,
                  }
                );
            });
          }
        }
      } catch (err: any) {
        if (process.env.REACT_APP_APP_INSIGHTS !== '')
          appInsights.trackException({
            exception: err,
            properties: {
              component: 'TtsApp',
              location: 'onDownload',
              tid: context?.user?.tenant?.id,
              uid: context?.user?.id,
            },
          });
      }
    }
  }

  function createNewProject() {
    createProject();
    setAudioTrack('');
    toast('A new project was created', { type: 'success' });
  }

  return (
    <PageControlsContainer>
      <ProjectControls>
        <SaveProjectModal
          isOpen={showSaveProjectModal}
          closeModal={() => setShowSaveProjectModal(false)}
        />

        <OpenProjectModal
          isOpen={showOpenProjectModal}
          closeModal={() => setShowOpenProjectModal(false)}
        />

        <DownloadModal
          format={format}
          setFormat={setFormat}
          isOpen={showDownloadModal}
          closeModal={() => setShowDownloadModal(false)}
          onDownload={handleDownload}
        />

        {isLoading && <Spinner size={2} />}

        <>
          <ControlButton
            label={t('TTS_NEW_PROJECT_BUTTON')}
            id="step-Fixed-Controls-New"
            onClick={() => createNewProject()}
          >
            <FilePlus size={24} weight="duotone" />
          </ControlButton>
          <ControlButton
            label={t('TTS_SAVE_AS_BUTTON')}
            id="step-Fixed-Controls-Save-As"
            onClick={() => saveProjectAs()}
          >
            <CloudArrowUp size={24} weight="duotone" />
          </ControlButton>
          <ControlButton
            label={t('TTS_SAVE_BUTTON')}
            id="step-Fixed-Controls-Save"
            onClick={() => handleSaveProject()}
          >
            <Cloud size={24} weight="duotone" />
          </ControlButton>

          <ControlButton
            label={t('TTS_OPEN_PROJECT_BUTTON')}
            id="step-Fixed-Controls-Open"
            onClick={() => setShowOpenProjectModal(true)}
          >
            <FileCloud size={24} weight="duotone" />
          </ControlButton>
        </>

        <ControlButton
          label={t('TTS_PLAY_ALL_BUTTON')}
          id="step-Fixed-Controls-PlayAll"
          onClick={() => handlePlayPage()}
        >
          <Queue size={24} weight="duotone" />
        </ControlButton>

        <ControlButton
          label={t('TTS_DOWNLOAD_BUTTON')}
          id="step-Fixed-Controls-Download"
          onClick={() => {
            if (limitReached) {
              toast.error(t('TTS_ERROR_LIMIT_REACHED'));
              return;
            }
            setShowDownloadModal(true);
          }}
        >
          <DownloadSimple size={24} weight="duotone" />
        </ControlButton>
      </ProjectControls>
    </PageControlsContainer>
  );
};
